"use client";

import { ContentNavEvents, trackEvent } from "@punchup/analytics";
import { EllipsisVertical, LinkIcon, Share2 } from "lucide-react";
import { copyToClipboard } from "@punchup/utils/clipboard";
import { genURLThisDomain, genWebAppURL } from "@punchup/utils/genAbsoluteURL";

import {
  PopoverOrDrawer,
  ShareModal,
} from "@punchup/ui-common/components/common";
import { toast } from "@punchup/ui-common/shadcn/ui/use-toast";
import { useState } from "react";
import { useVenueShowGroupHref } from "./show-groups/useVenueShowGroupHref";

interface TourDateActionsPopoverProps {
  /**
   * Fallback URL used when no show-group URL can be derived (e.g. the
   * consumer-pages gate is off, or `venueSlug`/`groupSlug` are missing).
   */
  copyUrl: string;
  /** When both are set, the popover prefers the show-group URL for copy/share. */
  venueSlug?: string;
  groupSlug?: string;
  shareData: {
    title: string;
    text: string;
  };
  onCopyLink?: () => void;
  onShare?: () => void;
  /** CSS color applied to the ellipsis trigger icon (e.g. for contrast against card backgrounds). */
  triggerColor?: string;
}

/**
 * Popover/drawer for tour date row actions (Copy Link, Share).
 * Forked from VideoCardActionsPopover in apps/web — the two are intentionally
 * duplicated so they can diverge over time.
 */
export default function TourDateActionsPopover({
  copyUrl,
  venueSlug,
  groupSlug,
  shareData,
  onCopyLink,
  onShare,
  triggerColor,
}: TourDateActionsPopoverProps) {
  const [actionsOpen, setActionsOpen] = useState<boolean>(false);
  const [showShareModal, setShowShareModal] = useState<boolean>(false);

  // Returns "" when the consumer-pages gate is off or when either slug is
  // missing — fall back to `copyUrl` in that case. The hook's path is
  // mount-relative, so we resolve it against the current host (matching the
  // title link in TourDateRow). The fallback `/e/{id}` is web-app only.
  const showGroupHref = useVenueShowGroupHref(venueSlug, groupSlug);
  const absoluteUrl = showGroupHref
    ? genURLThisDomain(showGroupHref)
    : genWebAppURL(copyUrl);

  const handleEllipsisClick = () => {
    setActionsOpen(true);
  };

  const handleCopyLinkClick = async () => {
    onCopyLink?.();

    const result = await copyToClipboard(absoluteUrl);

    if (result.success) {
      toast({
        title: "Link Copied to Clipboard!",
        variant: "success",
      });
      setActionsOpen(false);
    } else {
      console.error("Failed to copy text:", result.error);
      toast({
        title: "Failed to copy link",
        variant: "destructive",
      });
    }
  };

  const handleShareClick = () => {
    onShare?.();

    const fullShareData = {
      ...shareData,
      url: absoluteUrl,
    };

    let shareApiSupported = false;
    let shareableData: ShareData | null = null;

    if (typeof navigator !== "undefined" && Boolean(navigator.canShare)) {
      shareableData = fullShareData;
      shareApiSupported = navigator.canShare(fullShareData);

      if (!shareApiSupported) {
        const shareDataWithoutText: Partial<typeof fullShareData> = {
          ...fullShareData,
        };
        delete shareDataWithoutText.text;

        shareableData = shareDataWithoutText as ShareData;
        shareApiSupported = navigator.canShare(
          shareDataWithoutText as ShareData
        );
      }
    }

    const variant =
      shareApiSupported && shareableData ? "webShareApi" : "modal";

    trackEvent(ContentNavEvents.CLICKED_SHARE, {
      variant,
      location: "tourDateActions",
    });

    if (shareApiSupported && shareableData) {
      navigator
        .share(shareableData)
        .then(() => {
          trackEvent(ContentNavEvents.WEB_SHARE_SUCCEEDED, {
            variant,
            location: "tourDateActions",
            success: true,
          });
        })
        .catch((error) => {
          trackEvent(ContentNavEvents.WEB_SHARE_FAILED, {
            variant,
            location: "tourDateActions",
            error: error,
          });
        });
    } else {
      setShowShareModal(true);
    }
    setActionsOpen(false);
  };

  return (
    <>
      <PopoverOrDrawer
        isOpen={actionsOpen}
        onOpenChange={setActionsOpen}
        trigger={
          <button
            type="button"
            onClick={handleEllipsisClick}
            className="p-1.5 rounded-md transition-colors ease-in-out duration-200 bg-transparent hover:bg-white/10 active:bg-white/20"
          >
            <EllipsisVertical height={24} width={24} color={triggerColor} />
          </button>
        }
        align="end"
        side="bottom"
        className="z-[99] md:w-[200px] p-0 font-regular"
      >
        <ul>
          <li>
            <button
              type="button"
              onClick={handleCopyLinkClick}
              className="flex items-center justify-start w-full px-4 py-3 md:py-2 text-xl md:text-base text-gray-800 transition-colors duration-150 ease-in-out gap-x-3 md:gap-x-2 hover:bg-gray-50 rounded-md"
            >
              <div className="flex items-center justify-center">
                <LinkIcon className="h-5 w-5 md:h-4 md:w-4" />
              </div>
              <span>Copy Link</span>
            </button>
          </li>
          <li>
            <button
              type="button"
              onClick={handleShareClick}
              className="flex items-center justify-start w-full px-4 py-3 md:py-2 text-xl md:text-base text-gray-800 transition-colors duration-150 ease-in-out gap-x-3 md:gap-x-2 hover:bg-gray-50 rounded-md"
            >
              <div className="flex items-center justify-center">
                <Share2 className="h-5 w-5 md:h-4 md:w-4" />
              </div>
              <span>Share</span>
            </button>
          </li>
        </ul>
      </PopoverOrDrawer>

      <ShareModal
        open={showShareModal}
        setOpen={setShowShareModal}
        shareData={{
          ...shareData,
          url: absoluteUrl,
        }}
      />
    </>
  );
}
